import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Provider from "provider";
import logging from "utils/logging";
import {
  getDisplayStatus,
  neediestRound,
  PUBLISH_STATUS,
  DISPLAY_STATUS_AWAITING,
} from "../lifecycle";
import { launchGame, openBoard } from "./dashboardUtils";

const log = logging.getLogger("Dashboard Actions");

/**
 * Maps a game's lifecycle status to its primary CTA and its caret-menu items,
 * so the hero, grid cards, and queue rows all drive the same actions (PRD
 * §5.2 / §8.1). `notify(message, severity)` surfaces async outcomes via the
 * dashboard toast.
 *
 * Returns `buildActions(game) → { status, primary, menuItems }` where
 *   primary  = { label, onClick, icon?, intent? } | null
 *   menuItems = [{ label, onClick, destructive? }]
 */
const useGameActions = (notify) => {
  const navigate = useNavigate();

  return useCallback(
    (game) => {
      const id = game?.id;
      const status = getDisplayStatus(game);

      const go = (suffix) => () => navigate(`/manage/game/${id}/${suffix}`);
      const goEditor = go("editor");
      const goNeediest = () =>
        navigate(`/manage/game/${id}/editor/${neediestRound(game).round}`);
      const launch = () => launchGame(game, navigate);

      const run = (fn, successMsg) => async () => {
        try {
          await fn(id);
          if (successMsg && notify) notify(successMsg, "success");
        } catch (err) {
          log.error("Game action failed", err);
          if (notify) notify("Something went wrong. Please try again.", "error");
        }
      };

      const archive = {
        label: "Archive",
        onClick: run(Provider.archiveGame, "Game archived."),
        destructive: true,
      };

      switch (status) {
        case PUBLISH_STATUS.IN_PROGRESS:
          return {
            status,
            primary: { label: "Resume", onClick: launch, icon: "play" },
            menuItems: [
              { label: "Reset session", onClick: run(Provider.replayGame, "Session reset.") },
              { label: "Mark complete", onClick: run(Provider.markGamePlayed) },
              archive,
            ],
          };

        case PUBLISH_STATUS.READY:
          return {
            status,
            primary: { label: "Launch Game", onClick: launch, icon: "play" },
            menuItems: [
              { label: "Edit game", onClick: goEditor },
              { label: "Move back to draft", onClick: run(Provider.moveGameToDraft, "Moved back to draft.") },
              archive,
            ],
          };

        case DISPLAY_STATUS_AWAITING:
          return {
            status,
            primary: {
              label: "Mark as Ready",
              onClick: run(Provider.markGameReady, "Game marked ready."),
              icon: "check",
              intent: "ready",
            },
            menuItems: [
              { label: "Keep editing", onClick: goNeediest },
              archive,
            ],
          };

        case PUBLISH_STATUS.PLAYED:
          return {
            status,
            primary: { label: "View Results", onClick: () => openBoard(game), icon: "play" },
            menuItems: [
              { label: "Replay", onClick: run(Provider.replayGame, "Game reset to ready.") },
              archive,
            ],
          };

        case PUBLISH_STATUS.ARCHIVED:
          return {
            status,
            primary: { label: "Unarchive", onClick: run(Provider.unarchiveGame, "Game unarchived.") },
            menuItems: [
              { label: "Unarchive", onClick: run(Provider.unarchiveGame, "Game unarchived.") },
            ],
          };

        // draft < 100%
        default:
          return {
            status: PUBLISH_STATUS.DRAFT,
            primary: { label: "Keep editing", onClick: goNeediest, icon: "pencil" },
            menuItems: [archive],
          };
      }
    },
    [navigate, notify]
  );
};

export default useGameActions;
